import React from 'react';
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Typography } from '../ui';
import { AGAPELLA_ACCESS_MISMATCH_LINE } from '../../services/agapellaGate';
import type { AgapellaLiveGateState } from '../live/useAgapellaLiveGate';
import { AccessBadge } from './AccessBadge';

import { ON_ACCENT_TEXT } from '../../styles/accentText';
/** Слова закрытого плеера. Окно говорит подробнее — здесь только что случилось и куда нажать. */
export const LOCKED_PLAYER_TITLE = 'Эта Live-сессия входит в Agapēlla';
export const LOCKED_PLAYER_ACTION = 'Открыть доступ';
export const MISMATCH_PLAYER_ACTION = 'Попробовать ещё раз';
/** CORE-1780 — запись эфира или комнаты и практика из медиатеки. */
export const LOCKED_RECORDING_TITLE = 'Запись входит в Agapēlla';
export const LOCKED_PRACTICE_TITLE = 'Эта практика входит в Agapēlla';

interface Props {
  state: Exclude<AgapellaLiveGateState, 'open'>;
  coverUrl?: string | null;
  onOpenPaywall: () => void;
  onRetry: () => void;
  /** CORE-1780 — что закрыто: запись, практика. Без него — эфир. */
  title?: string;
  /** CORE-1780 — бейдж «Входит в Agapēlla» над замком (запись и практика). */
  badge?: boolean;
  testID?: string;
}

/**
 * CORE-1778 — плеер эфира, закрытый подпиской Agapēlla: обложка и кнопка,
 * открывающая окно, вместо вечного «Подключение…» (веб) и «Не удалось
 * подключиться» (натив). Один на обе платформы — только RN-примитивы.
 */
export const AgapellaLockedPlayer: React.FC<Props> = ({
  state, coverUrl, onOpenPaywall, onRetry, title = LOCKED_PLAYER_TITLE, badge = false, testID = 'live-player-agapella-locked',
}) => {
  const mismatch = state === 'mismatch';
  // `…-locked` → `…-open` / `…-retry`: эфир сохраняет прежние testID.
  const actionId = testID.replace(/-locked$/, '');
  return (
    <View style={styles.fill} testID={testID}>
      {coverUrl ? <Image source={{ uri: coverUrl }} style={styles.cover} resizeMode="cover" /> : null}
      <View style={styles.scrim} />
      <View style={styles.center}>
        {badge && !mismatch ? <AccessBadge price={null} includedInAgapella testID={`${testID}-badge`} /> : null}
        <Ionicons name={mismatch ? 'refresh-outline' : 'lock-closed-outline'} size={30} color="rgba(255,255,255,0.85)" />
        <Typography style={styles.title}>{mismatch ? AGAPELLA_ACCESS_MISMATCH_LINE : title}</Typography>
        <TouchableOpacity
          testID={mismatch ? `${actionId}-retry` : `${actionId}-open`}
          accessibilityRole="button"
          activeOpacity={0.85}
          onPress={mismatch ? onRetry : onOpenPaywall}
          style={styles.btn}
        >
          <Typography style={styles.btnText}>{mismatch ? MISMATCH_PLAYER_ACTION : LOCKED_PLAYER_ACTION}</Typography>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  fill: { ...StyleSheet.absoluteFillObject, backgroundColor: '#17181C', zIndex: 6 },
  cover: { ...StyleSheet.absoluteFillObject, width: '100%', height: '100%' },
  scrim: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.55)' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12, paddingHorizontal: 24 },
  title: { color: '#FFFFFF', fontSize: 16, textAlign: 'center' },
  btn: {
    marginTop: 4, paddingHorizontal: 22, paddingVertical: 11, borderRadius: 999,
    backgroundColor: '#E98245',
  },
  btnText: { color: ON_ACCENT_TEXT, fontSize: 14 },
});
